/**
 * Headlight Bridge Inspection — the pict-microapp manifest.
 *
 * This file IS the app. Everything the bridge app is — where it lands, which of the platform's
 * several hundred routes it exposes, which record sets are reachable, what its menu looks like — is
 * declared here and applied by pict-microapp against the platform configuration application.
 *
 * Adding a screen to the bridge app is therefore a one-line change: build it once in the platform
 * app (where every customer benefits), then list its route here.
 *
 * ROUTE SPELLINGS MUST MATCH THE HOST EXACTLY. The allow list is compared against the pattern the
 * platform app passes to `addRoute`, parameter names included — '/Asset/Workspace/:ID' matches,
 * '/Asset/Workspace/:IDAsset' does not. When a screen comes up missing, read
 * `_Pict.providers.MicroApp.blockedRoutes` in the console: the route you meant to keep will be
 * sitting in it, spelled the way the host actually registered it.
 */

const libBridgeNavigation = require('./BridgeInspection-Navigation-Graph.js');

module.exports =
{
	Name: 'Bridge Inspection',
	Hash: 'BridgeInspection',

	// The bridge dashboard (map / browse / overview of every bridge) is the landing screen, and is
	// also where a route this app does not carry bounces to.
	DefaultRoute: '/AssetDashboard',

	Routes:
	{
		Allow:
		[
			// --- Landing: the bridge dashboard -------------------------------------------------
			// :View is map | browse | overview.
			'/AssetDashboard',
			'/AssetDashboard/:View',

			// --- The bridge itself: the PhysicalAsset workspace ---------------------------------
			// Note the entity/prefix mismatch in the host: entity PhysicalAsset, route prefix Asset.
			// Tabs: overview | location | elements | defects | observations | documents | migrate.
			'/Asset/Workspace/:ID',
			'/Asset/Workspace/:ID/:Tab',
			// GUID bridges the host registers so a record-set row opens the workspace. Assets are
			// externally synced, so the host deliberately bounces Create back to the list.
			'/PSRS/PhysicalAsset/View/:GUIDRecord',
			'/PSRS/PhysicalAsset/Edit/:GUIDRecord',
			'/PSRS/PhysicalAsset/Create',

			// --- Inspections --------------------------------------------------------------------
			// :View is overview | list | calendar (plus year | month | week | timeline).
			'/InspectionSchedule',
			'/InspectionSchedule/:View',
			// Tabs: overview | assets | documents | inspectors | teams | timeline.
			'/Inspection/Workspace/:ID',
			'/Inspection/Workspace/:ID/:Tab',
			'/PSRS/Inspection/View/:GUIDRecord',
			// Schedule an inspection — the bespoke create screen, which also generates the document packet.
			// The /ForAsset/ form is how the bridge workspace's "Schedule inspection" action arrives.
			// Listed explicitly: the entity guard would admit these anyway, but a route this app depends on
			// should not be exposed only incidentally.
			'/PSRS/Inspection/Create',
			'/PSRS/Inspection/Create/ForAsset/:IDPhysicalAsset',

			// --- Teams ---------------------------------------------------------------------------
			// Tabs: overview | members | documents | projects | inspections | subteams | labs. The
			// schedule's team lane deep-links the :Tab form, so both are load-bearing.
			'/Team/Workspace/:ID',
			'/Team/Workspace/:ID/:Tab',
			'/PSRS/Team/View/:GUIDRecord',

			// --- Documents -----------------------------------------------------------------------
			// The throughput dashboard is reached from the nav as /DocumentThroughput/Inspection-Dossier,
			// and the inspection schedule's overview embeds the same provider inline.
			'/DocumentThroughput',
			'/DocumentThroughput/:Type',
			// These three are LITERAL routes in the host, shadowing the generic record-set list on
			// purpose (the generic one scans the whole Documents table). Keep all three.
			'/PSRS/Document/List',
			'/Documents/Browse',
			'/Documents/Explore',
			'/Document/Workspace/:ID',
			'/Document/Workspace/:ID/:Tab',
			'/PSRS/Document/View/:GUIDRecord',

			// --- Observations --------------------------------------------------------------------
			// Same shape: literal routes backed by the fast Solr-indexed list, three view modes.
			'/PSRS/Observation/List',
			'/Observations',
			'/ObservationExplorer',
			'/Observations/Explore',
			'/Observations/Map',
			'/Observation/Workspace/:ID',
			'/Observation/Workspace/:ID/:Tab',
			'/PSRS/Observation/View/:GUIDRecord',

			// --- Projects -------------------------------------------------------------------------
			// Not in the menu, but reachable: the inspection schedule links a project by name, and an
			// inspection's documents live under one. Dropping these would leave dead links on screens
			// this app does carry.
			'/Project/Workspace/:ID',
			'/Project/Workspace/:ID/:Tab',
			'/PSRS/Project/View/:GUIDRecord',

			// --- Asset setup (advanced) -------------------------------------------------------------
			'/AssetTypeManifests',
			'/AssetTypeManifests/:IDAssetType',
			'/AssetTypeDescriptor',
			'/AssetCatalogSync',

			// --- Documentation ----------------------------------------------------------------------
			'/Academy',
			'/Academy/:Topic',

			// The host's own alias for "go to the landing screen".
			'/Dashboard'
		],

		Deny: [],

		// The platform app registers ONE wildcard route per record-set verb
		// ('/PSRS/:RecordSet/List' and friends) covering all ~60 of its record sets, so they cannot
		// be dropped entity by entity at registration time. pict-microapp guards them instead: the
		// routes register, but an entity that is not listed here bounces to the dashboard.
		//
		// This is the list of record sets the bridge app exposes. It is deliberately a little wider
		// than the menu, because workspace association tabs link out to related records.
		EntityParameter: 'RecordSet',
		Entities:
		[
			'PhysicalAsset',
			'PhysicalAssetElement',
			'PhysicalAssetElementType',
			'PhysicalAssetMaterial',
			'PhysicalAssetDefect',
			'PhysicalAssetMetadata',
			'AssetType',
			'AssetClassification',
			'Inspection',
			'InspectionType',
			'Team',
			'Document',
			'Observation',
			'ObservationManifest',
			'Project',
			'User',
			'Organization',
			'Lab',
			'Equipment'
		],

		CatchUnmatched: true
	},

	Navigation:
	{
		Sections: libBridgeNavigation.BRIDGE_SECTIONS,
		// Keep the platform's capability / module gating, drop its super-user short-circuit — that
		// short-circuit exists to trim the platform's huge menu down for cross-customer staff, and
		// applied to this five-section graph it would hide the whole app from the people building it.
		SuperUserSeesEverything: true,
		HonorHostGates: true
	},

	Branding:
	{
		Title: 'Headlight Bridge Inspection'
	}
};
